/**
 * Jediný český komparátor jmen v repu (web, API, admin).
 *
 * Kolace `Intl.Collator("cs", { sensitivity: "accent" })`: Ch za H, Č za C, diakritika podle
 * normy (`kat` < `kát`), velikost písmen NEROZHODUJE (`compare("indie", "Indie") === 0`), takže
 * velké a malé písmeno téhož slova se v seznamu neroztrhnou. Binární `Array.prototype.sort()`
 * by dal `USA` před `Uganda` a malá písmena až za všechna velká.
 *
 * Collator vzniká jednou na úrovni modulu (sestavení stojí řádově víc než porovnání). Tenhle
 * soubor je jediná vestavěná výjimka měřidla `kontrola:razeni` (pravidlo `kolace`): jinde
 * se `localeCompare(` ani `new Intl.Collator(` nepíše, řadí se přes exporty odsud.
 */
const CZECH_COLLATOR = new Intl.Collator("cs", { sensitivity: "accent" });

/** České porovnání dvou textů; `null`/`undefined` = prázdný řetězec. */
export function compareCzech(a: string | null | undefined, b: string | null | undefined): number {
  return CZECH_COLLATOR.compare(a ?? "", b ?? "");
}

/**
 * České porovnání s ÚPLNÝM pořadím: `compareCzech`, a když ten vrátí shodu (texty lišící se
 * jen velikostí písmen, `bali` × `Bali`), rozhodne binární porovnání. Nula tedy znamená
 * totožné texty. Tam, kde se řadí v paměti a stránkuje (žebříček zájezdů, výřez aktivit
 * stránky měsíce), by jinak o pořadí shodných titulů rozhodovalo pořadí řádků z D1 a položka
 * by se mohla objevit na dvou stránkách zároveň, nebo na žádné.
 */
export function compareCzechStrict(a: string, b: string): number {
  return compareCzech(a, b) || (a < b ? -1 : a > b ? 1 : 0);
}

/**
 * Titul PŘED jménem: token s tečkou (i víc za sebou, `Ing. arch.` jako jeden). Tečka
 * a mezera (nebo konec) za ní jsou povinné, takže slovo, které titulem jen začíná
 * (`Ingrid`, `Drahomíra`, `Doc Holliday`), se nemění.
 */
const LEADING_TITLE =
  /^(?:Ing\.\s*arch\.|Ing\.|Bc\.|BcA\.|Mgr\.|MgA\.|MUDr\.|MDDr\.|MVDr\.|PhDr\.|RNDr\.|JUDr\.|PharmDr\.|PaedDr\.|ThDr\.|ThLic\.|ICDr\.|[Dd]oc\.|[Pp]rof\.|Dr\.)(?=\s|$)\s*/;

/** Titul ZA jménem, s čárkou i bez ní (`, Ph.D.`, ` CSc.`, `, MBA`). */
const TRAILING_TITLE =
  /\s*,?\s*(?:Ph\.\s?D\.|Th\.\s?D\.|ArtD\.|CSc\.|DrSc\.|DiS\.|MBA|LL\.\s?M\.|MSc\.|BSc\.)\s*,?\s*$/;

/**
 * Jméno člověka bez akademických titulů a se sloučenými mezerami: „Ing. Věra Kusá“ →
 * „Věra Kusá“, „Katarína Maruškinová, Ph.D.“ → „Katarína Maruškinová“. Iniciála
 * (`Misha Mae A. Seguros`) titulem není. Když by po odstranění nezbylo nic (jméno je jen
 * titul), vrátí jméno jen se sloučenými mezerami: prázdné jméno nejde zobrazit ani zapsat.
 * Je to klíč řazení lidí (`compareCzechPersonName`) i cílový tvar jména v datech.
 */
export function stripAcademicTitles(name: string): string {
  const collapsed = name.replace(/\s+/g, " ").trim();
  let rest = collapsed;
  for (let previous = ""; previous !== rest; ) {
    previous = rest;
    rest = rest.replace(LEADING_TITLE, "").replace(TRAILING_TITLE, "").trim();
  }
  return rest === "" ? collapsed : rest;
}

/**
 * České porovnání jmen lidí podle jména bez titulů (`stripAcademicTitles`), aby budoucí
 * „Ing. …“ nespadlo pod „I“. `null`/`undefined` = prázdný řetězec.
 */
export function compareCzechPersonName(
  a: string | null | undefined,
  b: string | null | undefined,
): number {
  return compareCzech(stripAcademicTitles(a ?? ""), stripAcademicTitles(b ?? ""));
}

/** Komparátor objektů podle `name` (`[...].sort(byCzechName)`). */
export function byCzechName<T extends { name?: string | null }>(a: T, b: T): number {
  return compareCzech(a.name, b.name);
}

/** Komparátor objektů podle `title`. */
export function byCzechTitle<T extends { title?: string | null }>(a: T, b: T): number {
  return compareCzech(a.title, b.title);
}

/** Nové pole: kopie `items` seřazená česky podle klíče. Vstup se nemění. */
export function sortCzech<T>(
  items: readonly T[],
  key: (item: T) => string | null | undefined,
): T[] {
  return [...items].sort((a, b) => compareCzech(key(a), key(b)));
}

/**
 * Řádek, který jde seřadit pořadím zemí. `parentId` je ZÁMĚRNĚ povinné: s nepovinným
 * („chybí = null") by projekce bez `parentId` připnula i místo povýšené na zemi (Bali má
 * `sortOrder` 140). Projekce, která `parentId` nenese, komparátor zemí nepoužije; webové řádky
 * s nepovinným `parentId` převádí volající.
 */
export interface CountryOrderItem {
  name: string;
  parentId: string | null;
  sortOrder?: number | null;
}

/**
 * Připnutá země = země bez rodiče s `sortOrder` > 0. Místo s `showAsCountry` má rodiče,
 * takže připnuté není nikdy, ať má `sortOrder` jakékoli.
 */
export function isPinnedCountry(item: CountryOrderItem): boolean {
  return item.parentId === null && (item.sortOrder ?? 0) > 0;
}

/**
 * Pořadí zemí: připnuté vzestupně podle `sortOrder` (shoda: `compareCzech`), za nimi všechny
 * ostatní `compareCzech` podle jména.
 */
export function compareCountryOrder(a: CountryOrderItem, b: CountryOrderItem): number {
  const pinnedA = isPinnedCountry(a);
  const pinnedB = isPinnedCountry(b);
  if (pinnedA !== pinnedB) return pinnedA ? -1 : 1;
  if (pinnedA) {
    const diff = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    if (diff !== 0) return diff;
  }
  return compareCzech(a.name, b.name);
}
